function fanFinderRoutes(answers) {
  if (["exposed", "covered"].includes(answers.exposure) || ["hazard", "animals"].includes(answers.constraint)) {
    return { families: [], status: "needs_check", reason: "application" };
  }
  if (!answers.exposure || !answers.constraint || answers.exposure === "unsure" || answers.constraint === "unsure") {
    return { families: [], status: "needs_check", reason: "unknown" };
  }
  if ((answers.placement === "wall" && answers.priority === "mobility") || (answers.placement === "mobile" && answers.priority === "height")) {
    return { families: [], status: "needs_check", reason: "placement" };
  }
  let families;
  if (answers.placement === "wall") families = ["wall"];
  else if (answers.placement === "raised") families = ["pedestal"];
  else if (answers.placement === "mobile") {
    families = answers.job === "distance" && answers.distance !== "short" ? ["drum", "floor"] : ["floor", "drum"];
  } else if (answers.priority === "height") families = ["pedestal", "wall"];
  else if (answers.job === "distance" && answers.distance !== "short") families = ["drum", "pedestal"];
  else if (answers.job === "multiple") families = ["pedestal", "wall", "floor"];
  else families = ["floor", "pedestal"];
  const needsCheck = answers.priority === "quiet" || answers.constraint === "materials" || answers.placement === "unsure" || answers.distance === "unsure" || answers.distance === "long";
  return { families: [...new Set(families)].slice(0, 3), status: needsCheck ? "needs_check" : "type_browse", reason: answers.priority === "quiet" ? "quiet" : answers.constraint === "materials" ? "materials" : answers.distance === "long" ? "obstacles" : "browse" };
}

class FanSelectionFramework extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = "true";
    this.initSceneSpotlight();
    this.dialog = this.querySelector("[data-finder-modal]");
    this.finder = this.querySelector("[data-finder]");
    if (!this.finder || !this.dialog) return;
    this.step = 0;
    this.answers = this.emptyAnswers();
    this.labels = {};
    try {
      this.content = JSON.parse(this.querySelector("[data-finder-content]")?.textContent || "{}");
    } catch {
      return;
    }
    this.panels = [...this.querySelectorAll("[data-step-panel]")];
    this.indicators = [...this.querySelectorAll("[data-step-indicator]")];
    this.count = this.querySelector("[data-step-count]");
    this.controls = this.querySelector("[data-controls]");
    this.back = this.querySelector("[data-back]");
    this.next = this.querySelector("[data-next]");
    this.handleDocumentClick = (event) => {
      const trigger = event.target.closest('a[href$="#fan-finder"]');
      if (!trigger) return;
      if (new URL(trigger.href, window.location.href).origin !== window.location.origin) return;
      event.preventDefault();
      this.openFinder(trigger);
    };
    this.handleDialogClick = (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) this.closeFinder();
    };
    document.addEventListener("click", this.handleDocumentClick);
    this.dialog.addEventListener("click", this.handleDialogClick);
    this.dialog.addEventListener("close", () => {
      document.body.classList.remove("fan-finder-is-open");
      this.lastTrigger?.focus({ preventScroll: true });
    });
    this.querySelector("[data-finder-close]")?.addEventListener("click", () => this.closeFinder());
    this.querySelectorAll("[data-finder-result-links]").forEach((region) => region.addEventListener("click", (event) => {
      if (event.target.closest("a")) this.closeFinder();
    }));
    this.querySelectorAll("[data-choice]").forEach((button) => button.addEventListener("click", () => this.choose(button)));
    this.querySelectorAll("[data-field-select]").forEach((select) => select.addEventListener("change", () => {
      this.answers[select.dataset.fieldSelect] = select.value;
      this.render();
    }));
    this.querySelector("[data-reset]")?.addEventListener("click", () => this.reset());
    this.back?.addEventListener("click", () => { this.step = Math.max(0, this.step - 1); this.render(); this.focusStep(); });
    this.next?.addEventListener("click", () => {
      if (!this.canContinue()) return;
      this.step = Math.min(4, this.step + 1);
      this.render();
      this.focusStep();
    });
    this.render();
    if (window.location.hash === "#fan-finder") this.openFinder();
  }

  initSceneSpotlight() {
    const spotlight = this.querySelector("[data-scene-spotlight]");
    if (!spotlight) return;
    const panels = [...spotlight.querySelectorAll("[data-scene-panel]")];
    const options = [...spotlight.querySelectorAll("[data-scene-select]")];
    const select = (option) => {
      const nextPanel = panels.find((panel) => panel.dataset.scenePanel === option.dataset.sceneSelect);
      const currentPanel = panels.find((panel) => !panel.hidden);
      if (!nextPanel || !currentPanel || nextPanel === currentPanel) return;
      const currentOption = options.find((item) => item.dataset.sceneSelect === currentPanel.dataset.scenePanel);
      currentPanel.hidden = true;
      nextPanel.hidden = false;
      if (currentOption) currentOption.hidden = false;
      option.hidden = true;
      nextPanel.querySelector(".scene-spotlight__caption a")?.focus({ preventScroll: true });
    };
    options.forEach((option) => {
      option.setAttribute("role", "button");
      option.addEventListener("click", (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        select(option);
      });
      option.addEventListener("keydown", (event) => {
        if (event.key !== " ") return;
        event.preventDefault();
        select(option);
      });
    });
  }

  emptyAnswers() {
    return { space: "", job: "", placement: "", distance: "", priority: "", exposure: "", constraint: "" };
  }

  focusStep() {
    this.panels.find((panel) => Number(panel.dataset.stepPanel) === this.step)?.querySelector("h3")?.focus({ preventScroll: true });
    this.querySelector(".finder-panel")?.scrollTo({ top: 0, behavior: "auto" });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
    this.dialog?.removeEventListener("click", this.handleDialogClick);
  }

  openFinder(trigger) {
    this.lastTrigger = trigger || document.activeElement;
    this.reset();
    this.sceneLabel = trigger?.dataset.finderScene || "";
    const space = trigger?.dataset.finderSpace;
    if (space) this.answers.space = space;
    if (typeof this.dialog.showModal === "function") this.dialog.showModal();
    else this.dialog.setAttribute("open", "");
    document.body.classList.add("fan-finder-is-open");
    this.focusStep();
  }

  closeFinder() {
    if (typeof this.dialog.close === "function") this.dialog.close();
    else {
      this.dialog.removeAttribute("open");
      document.body.classList.remove("fan-finder-is-open");
      this.lastTrigger?.focus();
    }
  }

  choose(button) {
    const field = button.dataset.field;
    this.answers[field] = button.dataset.value;
    this.labels[field] = button.dataset.label;
    this.querySelectorAll(`[data-choice][data-field="${field}"]`).forEach((choice) => {
      const selected = choice === button;
      choice.classList.toggle("is-selected", selected);
      choice.setAttribute("aria-pressed", String(selected));
    });
    this.render();
  }

  canContinue() {
    if (this.step === 0) return Boolean(this.answers.job);
    if (this.step === 1) return Boolean(this.answers.placement && this.answers.distance);
    if (this.step === 2) return Boolean(this.answers.priority);
    if (this.step === 3) return Boolean(this.answers.exposure && this.answers.constraint);
    return true;
  }

  route() {
    return fanFinderRoutes(this.answers);
  }

  reset() {
    this.step = 0;
    this.answers = this.emptyAnswers();
    this.labels = {};
    this.sceneLabel = "";
    this.querySelectorAll("[data-choice]").forEach((choice) => {
      choice.classList.remove("is-selected");
      choice.setAttribute("aria-pressed", "false");
    });
    this.querySelectorAll("[data-field-select]").forEach((select) => { select.value = ""; });
    this.render();
  }

  render() {
    this.panels.forEach((panel) => { panel.hidden = Number(panel.dataset.stepPanel) !== this.step; });
    this.indicators.forEach((indicator, index) => {
      indicator.classList.toggle("is-current", index === Math.min(this.step, 3));
      indicator.classList.toggle("is-complete", index < this.step);
      indicator.querySelector("span").textContent = index < this.step ? "✓" : String(index + 1);
    });
    if (this.count) this.count.textContent = this.content.step_count.replace("[current]", String(Math.min(this.step + 1, 4)));
    if (this.back) this.back.hidden = this.step === 0 || this.step === 4;
    if (this.next) {
      this.next.hidden = this.step === 4;
      this.next.disabled = !this.canContinue();
      this.next.firstChild.textContent = `${this.step === 3 ? this.content.see_routes : this.content.continue} `;
    }
    if (this.controls) this.controls.hidden = this.step === 4;
    if (this.step === 4) {
      const result = this.route();
      this.querySelector("[data-result-type]").textContent = result.families.length ? this.content.result_title : this.content.check_title;
      this.querySelector("[data-result-reason]").textContent = this.content.reasons[result.reason];
      const resultList = this.querySelector("[data-result-list]");
      resultList.replaceChildren();
      result.families.forEach((family) => {
        const item = document.createElement("li");
        const link = document.createElement("a");
        link.href = this.content.families[family].url;
        const title = document.createElement("strong");
        title.textContent = this.content.families[family].title;
        const copy = document.createElement("span");
        copy.textContent = this.content.families[family].reason;
        link.append(title, copy);
        item.append(link);
        resultList.append(item);
      });
      this.querySelector("[data-result-job]").textContent = this.labels.job;
      this.querySelector("[data-result-priority]").textContent = this.labels.priority;
      this.querySelector("[data-result-status]").textContent = this.content.status[result.status];
      const supportLink = this.querySelector("[data-result-support]");
      supportLink.hidden = result.status !== "needs_check";
      const resultLink = this.querySelector(".finder-result__action");
      resultLink.href = this.content.browse_url;
      this.dialog.dataset.finderStatus = result.status;
    }
  }
}

if (!customElements.get("fan-selection-framework")) customElements.define("fan-selection-framework", FanSelectionFramework);
